import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Button,
} from '@mui/material';
import {
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Language as LanguageIcon,
  Chat as ChatIcon,
  Upload as UploadIcon,
  Menu as MenuIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  AdminPanelSettings as AdminIcon,
  LockOutlined as LockIcon,
  MenuBook as DocumentationIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';

interface ModernHeaderProps {
  onMenuToggle?: () => void;
}

export const ModernHeader: React.FC<ModernHeaderProps> = ({ onMenuToggle: _ }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { currentLanguage, setLanguage } = useLanguage();
  const { mode, toggleTheme } = useCustomTheme();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';
  
  // Debug: Log user role for troubleshooting
  React.useEffect(() => {
    if (user) {
      console.log('🔍 ModernHeader - User object:', user);
      console.log('🔍 ModernHeader - User role:', user.role);
      console.log('🔍 ModernHeader - isAdmin:', isAdmin);
    }
  }, [user, isAdmin]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [langAnchorEl, setLangAnchorEl] = React.useState<null | HTMLElement>(null);
  const [navAnchorEl, setNavAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLangMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang as 'en' | 'fr');
    handleLangMenuClose();
  };

  const handleNavMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setNavAnchorEl(event.currentTarget);
  };

  const handleNavMenuClose = () => {
    setNavAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  return (
    <Box sx={{ 
      position: 'relative',
      zIndex: 1300, // Au-dessus du Drawer (1200)
      width: '100%',
    }}>
      {/* Main Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          background: mode === 'dark'
            ? 'linear-gradient(135deg, #052E16 0%, #14532D 50%, #166534 100%)' // Dark mode: deep green gradient
            : 'linear-gradient(135deg, #166534 0%, #16A34A 50%, #22C55E 100%)', // Light mode: green gradient
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          width: '100%',
          minHeight: 64,
        }}
      >
      <Toolbar sx={{ 
        minHeight: { xs: 56, sm: 64 },
        px: { xs: 1, sm: 2 },
        width: '100%',
        position: 'relative',
      }}>
        {/* Logo - Center (Desktop) */}
        <Box sx={{ 
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          gap: 0.75,
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'white',
              fontWeight: 700,
              fontSize: '1.25rem',
              letterSpacing: '0.5px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            <span style={{ color: 'white', fontSize: '1.25rem' }}>✨</span>
            Roland.IA
          </Typography>
        </Box>

        {/* Navigation - Mobile (burger menu à gauche) */}
        <IconButton 
          onClick={handleNavMenuClick}
          sx={{ 
            display: { xs: 'flex', sm: 'none' },
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            p: 1,
            mr: 1,
          }}
        >
          <MenuIcon />
        </IconButton>
        
        {/* Navigation - Desktop (à gauche) */}
        <Box sx={{ 
          display: { xs: 'none', sm: 'flex' }, 
          gap: 1,
        }}>
          <Button
            startIcon={<ChatIcon />}
            component={Link}
            to="/dashboard"
            sx={{
              color: location.pathname.startsWith('/dashboard') ? 'white' : 'rgba(255,255,255,0.7)',
              borderBottom: location.pathname.startsWith('/dashboard') ? '2px solid white' : 'none',
              borderRadius: 0,
              px: 2,
              py: 1,
              fontWeight: location.pathname.startsWith('/dashboard') ? 600 : 400,
              '&:hover': { 
                bgcolor: 'rgba(255,255,255,0.1)',
                color: 'white',
              },
            }}
          >
            {t('nav.chat', 'Chat')}
          </Button>
          <Button
            startIcon={<UploadIcon />}
            component={Link}
            to="/upload"
            sx={{
              color: location.pathname.startsWith('/upload') ? 'white' : 'rgba(255,255,255,0.7)',
              borderBottom: location.pathname.startsWith('/upload') ? '2px solid white' : 'none',
              borderRadius: 0,
              px: 2,
              py: 1,
              fontWeight: location.pathname.startsWith('/upload') ? 600 : 400,
              '&:hover': { 
                bgcolor: 'rgba(255,255,255,0.1)',
                color: 'white',
              },
            }}
          >
            {t('nav.upload', 'Upload')}
          </Button>
          <Button
            startIcon={<DocumentationIcon />}
            component={Link}
            to="/documentation"
            sx={{
              color: location.pathname.startsWith('/documentation') ? 'white' : 'rgba(255,255,255,0.7)',
              borderBottom: location.pathname.startsWith('/documentation') ? '2px solid white' : 'none',
              borderRadius: 0,
              px: 2,
              py: 1,
              fontWeight: location.pathname.startsWith('/documentation') ? 600 : 400,
              '&:hover': { 
                bgcolor: 'rgba(255,255,255,0.1)',
                color: 'white',
              },
            }}
          >
            {t('nav.documentation', 'Documentation')}
          </Button>
          {isAdmin && (
            <Button
              startIcon={<AdminIcon />}
              component={Link}
              to="/admin"
              sx={{
                color: location.pathname.startsWith('/admin') ? 'white' : 'rgba(255,255,255,0.7)',
                borderBottom: location.pathname.startsWith('/admin') ? '2px solid white' : 'none',
                borderRadius: 0,
                px: 2,
                py: 1,
                fontWeight: location.pathname.startsWith('/admin') ? 600 : 400,
                '&:hover': { 
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                },
              }}
            >
              {t('nav.admin', 'Admin')}
            </Button>
          )}
        </Box>
        
        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Right side controls */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 0.5, sm: 1 },
          flexShrink: 0,
        }}>
          {/* Theme Toggle */}
          <IconButton 
            onClick={toggleTheme}
            sx={{ 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              p: { xs: 1, sm: 1.5 },
            }}
            aria-label={mode === 'dark' ? t('theme.light', 'Switch to light mode') : t('theme.dark', 'Switch to dark mode')}
          >
            {mode === 'dark' ? (
              <LightModeIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            ) : (
              <DarkModeIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            )}
          </IconButton>

          {/* Language Toggle */}
          <IconButton 
            onClick={handleLangMenuClick}
            sx={{ 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              p: { xs: 1, sm: 1.5 },
            }}
          >
            <LanguageIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>

          {/* User Menu */}
          <IconButton 
            onClick={handleMenuClick}
            sx={{ 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              p: { xs: 1, sm: 1.5 },
            }}
          >
            <AccountIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </Box>

        {/* Navigation Menu (Mobile) */}
        <Menu
          anchorEl={navAnchorEl}
          open={Boolean(navAnchorEl)}
          onClose={handleNavMenuClose}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 180,
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.12)',
              bgcolor: 'background.paper',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }
          }}
        >
          <MenuItem 
            component={Link}
            to="/dashboard"
            onClick={handleNavMenuClose}
            selected={location.pathname.startsWith('/dashboard')}
            sx={{ minHeight: 48 }}
          >
            <ChatIcon sx={{ mr: 2, fontSize: 20 }} />
            {t('nav.chat', 'Chat')}
          </MenuItem>
          <MenuItem 
            component={Link}
            to="/upload"
            onClick={handleNavMenuClose}
            selected={location.pathname.startsWith('/upload')}
            sx={{ minHeight: 48 }}
          >
            <UploadIcon sx={{ mr: 2, fontSize: 20 }} />
            {t('nav.upload', 'Upload')}
          </MenuItem>
          <MenuItem 
            component={Link}
            to="/documentation"
            onClick={handleNavMenuClose}
            selected={location.pathname.startsWith('/documentation')}
            sx={{ minHeight: 48 }}
          >
            <DocumentationIcon sx={{ mr: 2, fontSize: 20 }} />
            {t('nav.documentation', 'Documentation')}
          </MenuItem>
          {isAdmin && (
            <MenuItem 
              component={Link}
              to="/admin"
              onClick={handleNavMenuClose}
              selected={location.pathname.startsWith('/admin')}
              sx={{ minHeight: 48 }}
            >
              <AdminIcon sx={{ mr: 2, fontSize: 20 }} />
              {t('nav.admin', 'Admin')}
            </MenuItem>
          )}
        </Menu>

        {/* Language Menu */}
        <Menu
          anchorEl={langAnchorEl}
          open={Boolean(langAnchorEl)}
          onClose={handleLangMenuClose}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 120,
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.12)',
              bgcolor: 'background.paper',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }
          }}
        >
          <MenuItem 
            onClick={() => handleLanguageChange('en')}
            selected={currentLanguage === 'en'}
            sx={{ minHeight: 40 }}
          >
            🇺🇸 English
          </MenuItem>
          <MenuItem 
            onClick={() => handleLanguageChange('fr')}
            selected={currentLanguage === 'fr'}
            sx={{ minHeight: 40 }}
          >
            🇫🇷 Français
          </MenuItem>
        </Menu>

        {/* User Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 200,
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.12)',
              bgcolor: 'background.paper',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t('auth.signedInAs')}
            </Typography>
            <Typography variant="body1" fontWeight={500} noWrap>
              {user?.username || user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem 
            component={Link}
            to="/settings/change-password"
            onClick={handleMenuClose}
            sx={{ minHeight: 48 }}
          >
            <LockIcon sx={{ mr: 2, fontSize: 20 }} />
            {t('nav.changePassword', 'Change Password')}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ minHeight: 48 }}>
            <LogoutIcon sx={{ mr: 2, fontSize: 20 }} />
            {t('auth.logout')}
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
    </Box>
  );
};

export default ModernHeader;
